import type { UUID } from "utils";

const BASE_URL = "/stablecoins";
const WALLETS_URL = `${BASE_URL}/wallets`;
// Beneficiaries, settlement destinations and notifications are tabs of the
// configuration section, so their pages live under its url.
const CONFIGURATION_URL = `${BASE_URL}/configuration`;
const CONFIGURATION_NOTIFICATIONS_URL = `${CONFIGURATION_URL}/notifications`;
const BENEFICIARIES_URL = `${CONFIGURATION_URL}/beneficiaries`;
const SETTLEMENT_DESTINATIONS_URL = `${CONFIGURATION_URL}/settlement-destinations`;
const SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL = `${SETTLEMENT_DESTINATIONS_URL}/bank-accounts`;
const SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL = `${SETTLEMENT_DESTINATIONS_URL}/crypto-wallets`;
const PAYMENTS_URL = `${BASE_URL}/payments`;
const PAYMENTS_TRANSFERS_URL = `${PAYMENTS_URL}/transfers`;
const PAYMENTS_SETTLEMENTS_URL = `${PAYMENTS_URL}/settlements`;
const PAYMENTS_CHANNELS_URL = `${PAYMENTS_URL}/channels`;

export const URL_PARAMS = {
  CHANNEL_ID: "channelId",
  CONFIGURE: "configure",
  WALLET_ID: "walletId",
} as const;

export const CONFIGURE_PARAM_VALUES = {
  DESTINATION: "destination",
  RESERVE: "reserve",
  SCHEDULE: "schedule",
} as const;

export const PAGE_URLS = {
  onboarding: `${BASE_URL}/onboarding`,

  // Wallets — list with Fiat / Crypto tabs
  wallets: WALLETS_URL,
  walletsFiat: `${WALLETS_URL}/fiat`,
  walletsCrypto: `${WALLETS_URL}/crypto`,
  walletsNew: `${WALLETS_URL}/new`,
  walletDetails: `${WALLETS_URL}/:walletId`,
  walletChannels: `${WALLETS_URL}/:walletId/channels`,
  walletChannelNew: `${WALLETS_URL}/:walletId/channels/new`,
  walletChannelDetails: `${WALLETS_URL}/:walletId/channels/:channelId`,
  walletSettlementSettings: `${WALLETS_URL}/:walletId/settlement`,
  walletSettlementConfigure: `${WALLETS_URL}/:walletId/settlement/configure`,
  walletRunSettlement: `${WALLETS_URL}/:walletId/run-settlement`,

  // Beneficiaries — configuration tab
  beneficiaries: BENEFICIARIES_URL,
  beneficiaryNew: `${BENEFICIARIES_URL}/new`,
  beneficiaryEdit: `${BENEFICIARIES_URL}/:beneficiaryId/edit`,

  // Settlement destinations — configuration tab (bank accounts + crypto wallets)
  settlementDestinations: SETTLEMENT_DESTINATIONS_URL,
  settlementDestinationsBankAccounts: SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL,
  settlementDestinationsCryptoWallets:
    SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL,
  bankAccountNew: `${SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL}/new`,
  bankAccountEdit: `${SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL}/:bankAccountId/edit`,
  cryptoWalletNew: `${SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL}/new`,
  cryptoWalletEdit: `${SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL}/:cryptoWalletId/edit`,

  // Configuration — customer-wide stablecoin settings, one tab per area
  configuration: CONFIGURATION_URL,
  configurationNotifications: CONFIGURATION_NOTIFICATIONS_URL,
  configurationNotificationsEdit: `${CONFIGURATION_NOTIFICATIONS_URL}/edit`,

  // Payments — unified money-movement list (payment / transfer / payout /
  // settlement / refund) with transfer + settlement details nested under it.
  payments: PAYMENTS_URL,
  paymentsTransfer: `${PAYMENTS_URL}/transfer`,
  paymentsTransferDetails: `${PAYMENTS_TRANSFERS_URL}/:transferId`,
  paymentsSettlementDetails: `${PAYMENTS_SETTLEMENTS_URL}/:settlementId`,
  channelPaymentDetails: `${PAYMENTS_CHANNELS_URL}/:paymentId`,
  paymentDetails: `${PAYMENTS_URL}/:id`,

  getLinkToPaymentDetails: (fiatTxId: UUID) => `${PAYMENTS_URL}/${fiatTxId}`,
  getLinkToTransferDetails: (transferId: UUID) =>
    `${PAYMENTS_TRANSFERS_URL}/${transferId}`,
  getLinkToSettlementDetails: (settlementId: UUID) =>
    `${PAYMENTS_SETTLEMENTS_URL}/${settlementId}`,
  /**
   * A channel payment can only be read through its channel. Rows that know the
   * channel pass it directly; rows that only know the wallet (the transactions
   * feed) pass that instead and the page resolves the channel.
   */
  getLinkToChannelPaymentDetails: (
    paymentId: UUID,
    scope: { channelId?: string | null; walletId?: string | null }
  ) => {
    const params = new URLSearchParams();

    if (scope.channelId) {
      params.set(URL_PARAMS.CHANNEL_ID, scope.channelId);
    } else if (scope.walletId) {
      params.set(URL_PARAMS.WALLET_ID, scope.walletId);
    }

    const query = params.toString();

    return `${PAYMENTS_CHANNELS_URL}/${paymentId}${query ? `?${query}` : ""}`;
  },
  getLinkToBeneficiaryEdit: (beneficiaryId: UUID) =>
    `${BENEFICIARIES_URL}/${beneficiaryId}/edit`,
  getLinkToBankAccountEdit: (bankAccountId: UUID) =>
    `${SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL}/${bankAccountId}/edit`,
  getLinkToCryptoWalletEdit: (cryptoWalletId: UUID) =>
    `${SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL}/${cryptoWalletId}/edit`,
  getLinkToWalletDetails: (walletId: UUID) => `${WALLETS_URL}/${walletId}`,
  getLinkToWalletChannels: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/channels`,
  getLinkToWalletChannelNew: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/channels/new`,
  getLinkToWalletChannelDetails: (walletId: UUID, channelId: UUID) =>
    `${WALLETS_URL}/${walletId}/channels/${channelId}`,
  getLinkToWalletSettlementSettings: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement`,
  getLinkToWalletRunSettlement: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/run-settlement`,
  getLinkToWalletSettlementConfigure: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement/configure`,
  getLinkToWalletSettlementReserve: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement?${URL_PARAMS.CONFIGURE}=${CONFIGURE_PARAM_VALUES.RESERVE}`,
  getLinkToWalletSettlementSchedule: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement?${URL_PARAMS.CONFIGURE}=${CONFIGURE_PARAM_VALUES.SCHEDULE}`,
  getLinkToWalletSettlementDestination: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement?${URL_PARAMS.CONFIGURE}=${CONFIGURE_PARAM_VALUES.DESTINATION}`,
} as const;
